import React, { memo } from "react";
import * as PropTypes from "prop-types";
import { databasePropType } from "../../../propTypes";
import { formatValue } from "../../../utilities";
import AsynchronousDataWidget from "../../widgets/AsynchronousDataWidget";
import ChangeByGniGroupChartWidget from "./ChangeByGniGroupChartWidget";
import ChangeByGniGroupTableWidget from "./ChangeByGniGroupTableWidget";
import ChangeByMethodChartWidget from "./ChangeByMethodChartWidget";
import ChangeInMethodMixChartWidget from "./ChangeInMethodMixChartWidget";
import MethodMixChartWidget from "./MethodMixChartWidget";
import SmallFigure from "../layout/SmallFigure";
import ThemeIntroductionController from "../layout/ThemeIntroductionController";
import TextOnly from "../layout/TextOnly";
import MultiFigure from "../layout/MultiFigure";
import LargeFigure from "../layout/LargeFigure";
import FigureOnly from "../layout/FigureOnly";
import TextBox from "../../box/TextBox";
import Reference from "../../widgets/Reference";
import TextColumns from "../layout/TextColumns";

const calculateSmallLineChartChange = point => {
  const previousPoint = point.series.data[point.point.index - 1];
  const previousY = previousPoint.y;

  const amount = point.y - previousY;

  return {
    period: `${previousPoint.x}-${point.x}`,
    amount,
    percentage: amount / previousY
  };
};

const formatSmallLineChartTooltip = (point, indicator) => {
  let text = `${point.series.name} ${indicator}, ${
    point.key
  }: <strong>${formatValue(point.y)}</strong>`;

  if (point.point.index > 0) {
    const change = calculateSmallLineChartChange(point);

    text += `<br />Change, ${change.period}: <strong>${formatValue(
      change.amount
    )}</strong> (<strong>${formatValue(change.percentage, true)}</strong>)`;
  }

  return text;
};

const Theme5 = props => {
  const database = props.database;
  const data = props.data;

  return (
    <section id="theme5" className="theme">
      <ThemeIntroductionController
        reference="05"
        title="Significant Yet Uneven Growth in the Decade Ahead"
      >
        In this section, we look at where the world will be if current trends
        persist for the coming decade. While it is impossible to predict what
        lies ahead, we know that the conversations taking place now will largely
        shape future trends. We make no assumptions about future economic,
        political, environmental, or cultural disruptions that might alter the
        trends we see today. Instead, we see this chapter as a starting point
        for discussions, providing a baseline assessment of where things could
        be to help inform conversations about what changes may be needed to
        support the future needs of contraceptive users.
      </ThemeIntroductionController>

      <TextOnly>
        <p>
          Both our level of certainty and the utility of detailed projections
          diminishes the further we look into the future. Because of this, we
          divide the coming decade into two parts: the near-term changes that
          may happen over the five years leading up to 2025, and the longer-term
          changes that may occur as we approach 2030. In this section,
          therefore, some more detailed results are only presented to 2025
          rather than 2030.
        </p>
        <p>
          In addition, our estimate of total users is far more certain than our
          estimates of what methods they may be using, given the many factors
          that could impact future changes in method mix. Instead of attempting
          to predict future changes we instead continue recent patterns of
          method mix changes into the future&mdash;results therefore provide a
          baseline of what things would look like if current patterns persist.
        </p>
        <p>
          Lastly, it is important to note that our estimates of consumption
          costs are evaluated at today’s prices, as we make no guesses about
          inflation or fluctuations in currency value.
        </p>
      </TextOnly>

      <TextOnly sectionHeading="Changes in Users of Contraception">
        <p>
          By the year 2020, the market for supplies in LMICs will be home to 487
          million users of contraception. According to current trends, that
          number will reach 569 million by 2030, an increase of 82.2 million
          users. The rate of increase, however, will begin to slow in the second
          half of the decade; from 2020 to 2025 the total number of women using
          contraception will grow by 44 million (+9%), but will only increase by
          38.2 users (+7%) in the five years leading to 2030.
        </p>
      </TextOnly>

      {/*Figure 5.1*/}
      <LargeFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ChangeByMethodChartWidget
              database={database}
              data={data ? data.changeByMethodUserChart : []}
              id="changeByMethodUserChart"
              reference="Figure 5.1"
              title="Change in Users, 2020–2030"
              subtitle="Comparison by Method"
              chooserId="figure-5-1"
              tooltipFormatter={function() {
                return formatSmallLineChartTooltip(this, "Users");
              }}
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv("theme5.changeByMethodUserChart", settings);
              }}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          Between 2020 and 2025, LAPMs and STMs will gain roughly equal numbers
          of users (+22.9 million and +21.1 million, respectively). From 2025 to
          2030, however, LAPMs will see a slightly larger increase with 21.4
          million users versus an additional 16.8 million users of STMs.
        </p>
        <p>
          Users of implants will see the largest increase in both absolute and
          relative terms. The number of women who use implants will grow from
          23.9 million in 2020 to 42.6 million in 2025 and 63.5 million in 2030;
          an increase of 39.6 million over ten years. However, despite this
          rapid growth, implants will still be one of the least used methods,
          accounting for just 8% of the total LMIC method mix in 2025 and 11% in
          2030. The growth trend for implants could be strongly influenced by
          financing from donors and governments to make implants affordable to
          more women. If this is the case, this trend may be particularly
          sensitive to a leveling off or decline in public sector support.
        </p>
        <p>
          Users of pills will see the largest decline: the number of women who
          use pills will decline from 91.7 million in 2020 to 83.3 million in
          2030, a decline of 8.41 million. Despite this overall decline,
          increases in the number of users of pills will occur in nearly half of
          the countries within the LMIC market.
        </p>
        <p>
          Because these changes in users are relatively small when compared to
          the total, changes to method mix are fairly minimal. Overall, users of
          LAPMs and STMs will each continue to represent roughly half of the
          method mix in 2030, as they do now.
        </p>
      </LargeFigure>

      {/*Figure 5.2*/}
      <LargeFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ChangeInMethodMixChartWidget
              database={database}
              data={data ? data.changeInMethodMixChart : []}
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv("theme5.changeInMethodMixChart", settings);
              }}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          The share of women using sterilization and IUDs will decline slightly
          between 2020 and 2030 (-4% points and -2% points, respectively), these
          declines will be offset by an increase in implants from 5% to 11% of
          the method mix (+6% points).
        </p>
        <p>
          The share of women using pills will decline by 4% points (from 19% to
          15%); this decline will be largely offset by an increase in male
          condoms. Injectables share of the method mix will hold steady at 15%.
        </p>
        <p>
          While the aggregate share of LAPM users within the overall method mix
          will remain similar, the gains will be unevenly distributed across
          countries. Implants will increase their share of method mix in all
          countries; recent data suggests that the largest gains are already
          happening within sub-Saharan Africa, particularly West Africa. Changes
          in sterilization and IUDs are more mixed&mdash;in some countries their
          share in the method mix is increasing, while in others it is stagnant
          or declining.
        </p>
      </LargeFigure>

      {/*Box 5.1*/}
      <FigureOnly>
        <TextBox
          reference="Box 5.1"
          title="Country-Level Projections: Users"
          large={true}
        >
          <TextColumns>
            <p>
              From country to country, there will be wide variations in changes
              in the number of women using contraception over the coming five
              years.
              <Reference marker={"&dagger;"}>
                At the country level, our projections for changes in the method
                mix of users are informed by recent patterns of change at the
                regional level, and not by country-specific policies or
                implementation plans, which could also influence method mix
                shifts.
              </Reference>
              The largest increase will be in India (+11.1 million). Ethiopia
              and Nigeria will see the next largest increase in users though the
              gains are much smaller in absolute terms (just under 3 million
              users in each country). Nonetheless, these gains will be much more
              rapid in relative terms&mdash;increasing by 34% in Ethiopia and
              38% in Nigeria, compared to only 8% in India. The largest relative
              increase, however, will be in South Sudan, where the number of
              users will increase by 168% from 99 million to 266 million.
            </p>
            <p>
              By contrast, nine countries are projected to experience slight
              declines in their total user numbers from 2020 to 2025: Russian
              Federation, Thailand, Ukraine, Korea DPR, Romania, Cuba, Belarus,
              Bulgaria, and Georgia. These are countries where contraceptive use
              is already high and population growth is slowing. In all cases
              these declines are very small, ranging from 1% to 10% in each
              country.
            </p>
            <p>
              Increases in pill users are projected in half of the countries in
              the LMIC market. These countries will gain in aggregate 5.23
              million pill users, and just over one-third of this increase will
              come from three countries: Egypt (+794 million), Philippines (+628
              million), and Indonesia (+445 million). These gains, however, are
              offset by a larger decline in pill users in the remaining
              countries, which will see a collective decline of 8.41 million
              pill users from 2020 to 2025.
            </p>
            <p>
              These changes in users are driven by divergent patterns in method
              mix. For example, when it comes to long-term methods, Nigeria will
              see a large increase in implants’ share of the method mix, from
              20% to 32%. On the other hand, IUD use will remain low. In
              Indonesia, both implants and IUDs will gain similar shares of the
              method mix, with implants increasing from 7% to 10% and IUDs
              increasing from 10% to 13%. In Mexico, the gain in implants (7% to
              9%) will be almost entirely offset by the decline in IUDs (19% to
              18%).
            </p>
          </TextColumns>
        </TextBox>
      </FigureOnly>

      <MultiFigure
        largeFigure={
          // Table 5.1
          <AsynchronousDataWidget loaded={!!data}>
            <ChangeByGniGroupTableWidget
              database={database}
              data={data ? data.changeByGniGroupUserTable : []}
              id="changeByGniGroupUserTable"
              reference="Table 5.1"
              title="Change in Users, 2020–2030"
              subtitle="Segmented by GNI Group"
              chooserId="table-5-1"
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv(
                  "theme5.changeByGniGroupUserTable",
                  settings
                );
              }}
            />
          </AsynchronousDataWidget>
        }
        smallFigure={
          // Figure 5.3
          <AsynchronousDataWidget loaded={!!data}>
            <ChangeByGniGroupChartWidget
              database={database}
              data={data ? data.changeByGniGroupUserChart : []}
              id="changeByGniGroupUserChart"
              reference="Figure 5.3"
              title="Change in Users, 2020–2030"
              subtitle="Comparison by GNI Group"
              chooserId="figure-5-4"
              tooltipFormatter={function() {
                return formatSmallLineChartTooltip(this, "Users");
              }}
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv(
                  "theme5.changeByGniGroupUserChart",
                  settings
                );
              }}
            />
          </AsynchronousDataWidget>
        }
      >
        <h4 className="subsection-heading">Changes in Users by GNI Group</h4>
        <p>
          Between 2020 and 2030 the largest increase in users in absolute terms
          will be seen within lower-middle-income countries, where total users
          will increase by 47.2 million. Just over half of this increase will
          take place in the five years leading up to 2025 (+26.3 million), with
          slightly fewer users being added in the later five years (+21
          million). Due to the large user base in these countries, however, this
          increase represents only a 9% change from 2020 to 2025 and 16%
          increase over the entire decade leading up to 2030.
        </p>
        <p>
          Low-income countries will see the most rapid growth, increasing by
          nearly 30% between 2020 and 2025 alone. While the pace of increase
          will decline slightly, low-income countries will add more users in the
          five years leading to 2030 (+15.6 million) that will be added from
          2020 to 2025 (+14.3 million). In total over the 10 years leading up to
          2030, this GNI group will see a 60% increase in users (+29.9 million).
        </p>
        <p>
          Finally, growth in upper-middle-income countries will nearly stagnate
          because many of the countries in this group already have high levels
          of contraceptive use and slower rates of population growth. Less than
          half as many users will be added in the five-year period after 2025,
          than the five years preceding it. This GNI group will only experience
          a 4% increase (+5.11 million) over the entire decade leading up to
          2030.
        </p>
      </MultiFigure>

      {/*Figure 5.4*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!database}>
            <MethodMixChartWidget
              database={database}
              data={data ? data.methodMixChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          While there will be declines in IUD users between 2020 and 2030 within
          upper-middle-income countries (-3.59 million), this method will gain
          users within the other two GNI groups (+221 thousand in low-income
          countries and +643 thousand in lower-middle-income countries). Users
          of implants will increase across all three GNI groups; just over half
          of the increase in implant users will come from lower-middle-income
          countries (+20.3 million); a significant share of the increase (35%)
          will also be seen among low-income countries (+13.9 million).
          Low-income countries will also experience the largest increase in
          terms of injectables (+11.4 million).
        </p>
        <p>
          Pill use will decline in all three GNI groups, though the decline in
          low-income countries will be relatively small (-774 thousand).
          Lower-middle-income countries will also see a small decline in
          injectable use (-581 thousand). Within upper-middle-income countries,
          three methods are projected to decline (IUDs, pills, and
          sterilization). Because overall increases in users is limited in
          within this GNI group declines in the method mix of methods are more
          likely to result in actual declines in users. Overall, despite these
          changes, the method mix will stay fairly similar within each GNI group
          over the coming decade.
        </p>
      </SmallFigure>

      <TextOnly sectionHeading="Changes in Consumption Costs">
        <p>
          As the total number of users of contraception grows, so too will the
          costs. In 2020, the cost of all supplies consumed by users in the LMIC
          market will be $3.51 billion. By 2025, that figure will increase to
          $3.6 billion at today’s prices, with the cumulative cost over the
          five-year period (2021&ndash;2025) totaling $17.8 billion. By 2030,
          the total consumption cost across the LMIC market will $3.63 billion,
          an increase of $120 million over the ten-year period.
        </p>
        <p>
          Just as the growth in the number of users is projected to slow down
          over the course of the coming decade, so too will the increase in
          consumption costs. From 2020 to 2025, consumption costs will grow by
          $87.7 million (+2%), while in the five years leading to 2030, they
          will grow by less than half that amount&mdash;$32.4 million (+1%).
        </p>
      </TextOnly>

      {/*Figure 5.5*/}
      <LargeFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <ChangeByMethodChartWidget
              database={database}
              data={data ? data.changeByMethodCostChart : []}
              id="changeByMethodCostChart"
              reference="Figure 5.5"
              title="Change in Consumption Cost, 2020–2030"
              subtitle="Comparison by Method"
              chooserId="figure-5-5"
              tooltipFormatter={function() {
                return formatSmallLineChartTooltip(this, "Cost");
              }}
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv("theme5.changeByMethodCostChart", settings);
              }}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          Overall, the share of total consumption cost attributed to each method
          will stay fairly consistent between 2020 and 2025. There are, however,
          some notable changes.
        </p>
        <p>
          Increases in the number of users of implants and injectables will
          drive most of the increase in consumption cost. The consumption cost
          of implants will increase by $74.9 million between 2020 and 2025,
          while the consumption cost of injectables will increase by $72.5
          million. A sizeable increase will also be seen in the consumption cost
          of male condoms (+$43.2 million).
        </p>
        <p>
          Increases in the consumption costs of implants, injectables and
          condoms will, however, be largely offset by declines in the costs of
          other methods. Most notably, the consumption cost of pills will
          decline from $2.37 billion in 2020 to $2.27 billion in 2025.
          Nonetheless, pills will continue to dominate consumption costs,
          representing 67% of the total cost of supplies in 2020, 63% of the
          total cost in 2025, and 58% of costs in 2030.
        </p>
      </LargeFigure>

      {/*Box 5.2*/}
      <FigureOnly>
        <TextBox
          reference="Box 5.2"
          title="Country-Level Projections: Costs"
          large={true}
        >
          <TextColumns>
            <p>
              Over the period from 2020 to 2025, the largest increases in
              consumption costs will be seen in the Russian Federation (+$16.5
              million), Nigeria (+$14.7 million), and Ethiopia (+$13.5 million).
              Interestingly, the Russian Federation will see the largest
              increase despite experience a slight decline in user numbers. The
              increase in cost will result mainly from a shift away from IUDs
              towards pills and condoms. On the other hand, twenty-four
              countries will see costs decline between 2020 and 2025, despite an
              increase in the number of users in most of these countries. The
              declining costs will be nearly all attributable to shifts in
              method mix that result in lower consumption cost.
            </p>
            <p>
              Nearly half of all countries will see increases in the consumption
              cost of pills between 2020 and 2025. The largest of these
              increases will be in the Russian Federation (+$20.1 million), the
              Philippines (+$7.78 million), and Iraq (+$6.03 million). By
              contrast, Egypt and Indonesia are expected to be among the top
              three countries to see an increase in pill users, and yet they
              will rank fourth and eighth, respectively, in terms of costs due
              to differences in the price of pills and the relative
              contributions of the public and private sectors to pill use.
            </p>
            <p>
              Despite declining consumption costs of all supplies in Brazil, the
              country will see the largest absolute increase in costs associated
              with injectables from 2020 to 2025 (+$15.2 million), followed by
              Ethiopia (+$8.82 million) and Nigeria (+$7.59 million). When it
              comes to implants the largest increases in costs will be seen in
              India
              <Reference marker={"&Dagger;"}>
                While contraceptive implants are not currently provided in India
                regional shifts in method mix would suggest very low levels of
                implant use by 2025 (2% of the method mix).
              </Reference>
              (+$8.87 million), Brazil (+$6.81 million), and Nigeria (+$6.15
              million).
            </p>
            <p>
              Overall, Brazil will still have the highest consumption cost of
              any one country by far, though its share of the total will decline
              slightly from 30% in 2020 to 28% in 2025.
            </p>
          </TextColumns>
        </TextBox>
      </FigureOnly>

      <MultiFigure
        largeFigure={
          // Table 5.2
          <AsynchronousDataWidget loaded={!!data}>
            <ChangeByGniGroupTableWidget
              database={database}
              data={data ? data.changeByGniGroupCostTable : []}
              id="changeByGniGroupCostTable"
              reference="Table 5.2"
              title="Change in Consumption Cost, 2020-2030"
              subtitle="Segmented by GNI Group"
              chooserId="table-5-2"
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv(
                  "theme5.changeByGniGroupCostTable",
                  settings
                );
              }}
            />
          </AsynchronousDataWidget>
        }
        smallFigure={
          // Figure 5.6
          <AsynchronousDataWidget loaded={!!data}>
            <ChangeByGniGroupChartWidget
              database={database}
              data={data ? data.changeByGniGroupCostChart : []}
              id="changeByGniGroupCostChart"
              reference="Figure 5.6"
              title="Change in Consumption Cost, 2020–2030"
              subtitle="Comparison by GNI Group"
              chooserId="figure-5-6"
              tooltipFormatter={function() {
                return formatSmallLineChartTooltip(this, "Cost");
              }}
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv(
                  "theme5.changeByGniGroupCostChart",
                  settings
                );
              }}
            />
          </AsynchronousDataWidget>
        }
      >
        <h4 className="subsection-heading">
          Changes in Consumption Costs by GNI Group
        </h4>
        <p>
          From 2020 to 2030 low-income countries will see the largest gain
          consumption costs in both relative (+62%) and absolute (+$144 million)
          terms. Despite these gains, they will still only account for 10% of
          the total consumption cost in 2030.
        </p>
        <p>
          Among lower-middle-income countries consumption costs will increase by
          $101 million, a 10% increase over ten years (2021&ndash;2030). In
          contrast, upper-middle-income countries will see a decline in
          consumption costs (-$125 million), though they will still account to
          the largest share of consumption costs (60%) across the LMIC market.
        </p>
        <p>
          The projected changes in consumption cost by GNI group will become
          even more divergent as 2030 approaches. Low-income countries will gain
          even more cost in absolute terms from 2026-2030 (+$74.5 million) than
          in the five years leading up to 2025 (+$69.7 million). By contrast,
          the increase in costs in lower-middle-income countries will be nearly
          $20 million smaller (+$40.8 million) in the five years after 2025 than
          the gains in the five-year leading to 2025 (+$60.3 million). Finally,
          the decline in cost in upper-middle-income countries will accelerate;
          the decline will nearly double from $42.3 million from 2020 to 2025 to
          $82.9 million from 2025 to 2030. However, given the magnitude of
          consumption costs in this GNI group, these declines represent only a
          2% and 4% decline respectively.
        </p>
      </MultiFigure>

      <TextOnly>
        <p>
          While upper middle-income countries will add less than half as many
          implant users as low-income countries over the coming decade, the
          gains in implant consumption cost between these two groups will be
          similar (+$41.5 million versus +$47.35 million). This is due to the
          higher unit prices of implants within upper-middle-income countries.
          Similarly, while upper middle-income countries will only account for
          18% of the increase in injectable users from 2020 to 2030, they will
          account for 40% of the increase in cost over this same period.
        </p>
        <p>
          As this section has revealed, changes in consumption cost reflect the
          growth in users, shifting patterns in the method mix, the relative
          role of the public and private sector in service provision, and
          country level differences in prices. Just as discussed in Theme 4,
          changes in costs are not simply a function of changes in users. As our
          community plans for the future, it is important to not only consider
          where growth in users will be taking place, but also how the
          consumption costs associated with those users may change.
        </p>
      </TextOnly>
    </section>
  );
};

Theme5.propTypes = {
  database: databasePropType,
  data: PropTypes.object,
  onWidgetChange: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired
};

export default memo(Theme5);
